import { useCallback, useEffect, useState } from "react";
import {
  DEMOCRACY_VOTE_MODES,
  type DemocracyVoteMode,
  type GameMode,
  type GameSummaryDto,
  type Side,
  type TimeControl,
  type UserDto,
} from "@chess/shared";

import { ApiError, closeLobby, createGame, joinGame, listGames, listOpenDemocracyGames } from "./api.js";
import { TimeControlPicker, describeTimeControl } from "./TimeControlPicker.js";
import { navigate } from "./router.js";

function describe(game: GameSummaryDto, userId: string): string {
  const mySide = game.players.find((player) => player.userId === userId)?.side;
  if (game.mode === "democracy") {
    if (game.status === "waiting") {
      return `${game.whiteCount ?? 0} White · ${game.blackCount ?? 0} Black · ${game.readyCount ?? 0}/${game.players.length} ready`;
    }
    if (game.status === "finished") return game.result === "draw" ? "Drawn" : `${game.result} wins`;
    return game.turn === mySide ? "Your team to vote" : `${game.turn} team voting`;
  }

  const opponent = game.players.find((player) => player.userId !== userId);
  if (game.status === "waiting") return "Waiting for an opponent";
  if (game.status === "finished") return game.result === "draw" ? "Drawn" : game.result === mySide ? "You won" : "You lost";
  return game.turn === mySide ? "Your move" : `${opponent?.username ?? "Opponent"}'s move`;
}

function voteModeName(mode: DemocracyVoteMode | null): string {
  return DEMOCRACY_VOTE_MODES.find((entry) => entry.mode === mode)?.name ?? "Lottery mode";
}

export function Lobby({ user }: { user: UserDto }) {
  const [games, setGames] = useState<GameSummaryDto[] | null>(null);
  const [openDemocracy, setOpenDemocracy] = useState<GameSummaryDto[] | null>(null);
  const [mode, setMode] = useState<GameMode>("1v1");
  const [timeControl, setTimeControl] = useState<TimeControl | null>({
    initialSeconds: 600,
    incrementSeconds: 5,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [mine, open] = await Promise.all([listGames(), listOpenDemocracyGames()]);
      setGames(mine);
      setOpenDemocracy(open);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't load games.");
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function handleCreate(side: Side | "random") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const game = await createGame(side, timeControl, mode);
      navigate(`/g/${game.id}`);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't start a game.");
      setBusy(false);
    }
  }

  async function handleJoin(gameId: string, side: Side) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await joinGame(gameId, side);
      navigate(`/g/${gameId}`);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't join that team.");
      setBusy(false);
    }
  }

  async function handleClose(game: GameSummaryDto) {
    setClosingId(game.id);
    setError(null);
    try {
      await closeLobby(game.id);
      setGames((current) => current?.filter((entry) => entry.id !== game.id) ?? null);
      setOpenDemocracy((current) => current?.filter((entry) => entry.id !== game.id) ?? null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't close that lobby.");
    } finally {
      setClosingId(null);
    }
  }

  return (
    <div className="lobby">
      <section className="card">
        <div className="section-heading">
          <div>
            <h2>New game</h2>
            <p className="muted">Choose a format to get started.</p>
          </div>
        </div>

        <div className="mode-picker" role="group" aria-label="Game format">
          <button
            type="button"
            className={mode === "1v1" ? "mode-picker__active" : "button--secondary"}
            onClick={() => setMode("1v1")}
          >
            1v1 chess
          </button>
          <button
            type="button"
            className={mode === "democracy" ? "mode-picker__active" : "button--secondary"}
            onClick={() => setMode("democracy")}
          >
            Team chess
          </button>
        </div>

        <p className="muted">
          {mode === "democracy"
            ? "Build two teams of up to ten, then choose the clock and vote rule together in the lobby."
            : "Start a game, then send the link to whoever you want to play."}
        </p>

        {mode === "1v1" ? <TimeControlPicker value={timeControl} onChange={setTimeControl} /> : null}

        {mode === "democracy" ? (
          <button type="button" disabled={busy} onClick={() => void handleCreate("random")}>
            Create lobby
          </button>
        ) : (
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void handleCreate("random")}>
              Random side
            </button>
            <button type="button" className="button--secondary" disabled={busy} onClick={() => void handleCreate("white")}>
              Play white
            </button>
            <button type="button" className="button--secondary" disabled={busy} onClick={() => void handleCreate("black")}>
              Play black
            </button>
          </div>
        )}
        {error ? <p className="error" role="alert">{error}</p> : null}
      </section>

      <section className="card">
        <div className="section-heading">
          <div><h2>Open Team chess lobbies</h2><p className="muted">Join a team before its lobby starts.</p></div>
          <button type="button" className="button--secondary button--compact" onClick={() => void refresh()}>Refresh</button>
        </div>
        {openDemocracy === null ? <p className="muted">Loading…</p> : openDemocracy.length === 0 ? <p className="muted">No open team lobbies yet.</p> : (
          <ul className="gamelist">
            {openDemocracy.map((game) => (
              <li key={game.id} className="gamelist__democracy">
                <button type="button" className="gamelist__main gamelist__item" onClick={() => navigate(`/g/${game.id}`)}>
                  <span className="gamelist__id">{game.lobbyName}</span>
                  <span className="muted">{describe(game, user.id)}</span>
                </button>
                <span className="tag">{voteModeName(game.democracyVoteMode)} · {describeTimeControl(game.timeControl)}</span>
                <div className="gamelist__actions">
                  <button type="button" className="button--secondary button--compact" disabled={busy || (game.whiteCount ?? 0) >= 10} onClick={() => void handleJoin(game.id, "white")}>Join White</button>
                  <button type="button" className="button--secondary button--compact" disabled={busy || (game.blackCount ?? 0) >= 10} onClick={() => void handleJoin(game.id, "black")}>Join Black</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Your games</h2>
        {games === null ? <p className="muted">Loading…</p> : games.length === 0 ? <p className="muted">Nothing yet. Start one above.</p> : (
          <ul className="gamelist">
            {games.map((game) => (
              <li key={game.id}>
                <button type="button" className="gamelist__item" onClick={() => navigate(`/g/${game.id}`)}>
                  <span className="gamelist__main"><span className="gamelist__id">{game.lobbyName}</span><span className="muted">{describe(game, user.id)}</span></span>
                  <span className="tag">{game.mode === "democracy" ? `Team chess · ${voteModeName(game.democracyVoteMode)}` : "1v1"} · {describeTimeControl(game.timeControl)}</span>
                </button>
                {game.status === "waiting" && game.createdBy === user.id ? (
                  <button
                    type="button"
                    className="button--secondary button--compact"
                    disabled={closingId !== null}
                    onClick={() => void handleClose(game)}
                  >
                    {closingId === game.id ? "Closing…" : "Close lobby"}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
